import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import {
  ChevronDown,
  Info,
  FileText,
  Receipt,
  Stethoscope,
  AlertTriangle,
  UserX,
  MessageSquare,
  Mail,
  BarChart3,
  ClipboardCheck,
} from "lucide-react";
// Objeto com informações específicas para cada tipo de relatório
const informacoesRelatorioPorTipo = {
  // Padrão para quando nenhum relatório é selecionado
  default: {
    title: "Selecione um relatório",
    description:
      "Escolha um tipo de relatório para visualizar informações específicas.",
    icon: <Info />,
    severity: "info",
  },
  contabilidadeAnalitica: {
    title: "Relatório de Contabilidade Analítica",
    description:
      "Este relatório mostra, para cada dentista, quanto trouxe à clínica em tratamentos executados e quanto gastou em despesas e materiais, dentro de um período escolhido. Permite ver rapidamente quais dentistas são mais rentáveis e identificar desequilíbrios entre o que cada um produz e o que custa à clínica.",
    detailedInfo: [
      {
        label: "Período do Relatório",
        description:
          "Os tratamentos só são contabilizados no mês em que são concluídos. Despesas e materiais entram pela data em que foram pagos ou utilizados.",
        color: "#1976d2",
      },
      {
        label: "Entradas — Tratamentos de Plano",
        description:
          "Valor dos procedimentos de planos de tratamento que foram concluídos no período. Procedimentos ainda em curso não contam até estarem terminados.",
        color: "#2e7d32",
      },
      {
        label: "Tratamentos Partilhados entre Dentistas",
        description:
          "Quando um tratamento foi feito em várias sessões por dentistas diferentes, o valor é dividido proporcionalmente — cada dentista recebe a parte correspondente às sessões que executou.",
        color: "#388e3c",
      },
      {
        label: "Valor Considerado",
        description:
          "É sempre o valor total do tratamento, independentemente de o cliente ser particular ou ter cobertura de uma instituição. Se houver cobertura, considera-se a soma da parte do cliente e da parte da instituição.",
        color: "#43a047",
      },
      {
        label: "Entradas — Tratamentos Isolados",
        description:
          "Tratamentos feitos fora de um plano (avulsos). Cada tratamento atendido conta pelo seu preço particular.",
        color: "#66bb6a",
      },
      {
        label: "Sessões em Tratamentos Isolados",
        description:
          "Quando um tratamento isolado é feito em várias sessões (mesmo cliente, mesmo tratamento, mesmo dente), o sistema agrupa-as automaticamente e divide o valor pelo número de sessões. Sessões muito espaçadas no tempo são tratadas como tratamentos separados.",
        color: "#81c784",
      },
      {
        label: "Gastos — Despesas",
        description:
          "Despesas já pagas que estão associadas ao dentista (por exemplo, comissões, honorários ou outros pagamentos imputados a ele).",
        color: "#d32f2f",
      },
      {
        label: "Gastos — Materiais Utilizados",
        description:
          "Custo dos produtos retirados do stock onde o dentista foi indicado como beneficiário. Calculado pelo preço de compra do material multiplicado pela quantidade utilizada.",
        color: "#e53935",
      },
      {
        label: "Saldo por Dentista",
        description:
          "Diferença entre o que o dentista trouxe à clínica e o que custou. Verde quando positivo (contribui mais do que custa), vermelho quando negativo (custos superiores às entradas).",
        color: "#f57c00",
      },
      {
        label: "Volume de Trabalho",
        description:
          "Para cada dentista vê-se o número de sessões realizadas e o número de tratamentos concluídos. O número de tratamentos pode aparecer com decimais quando um tratamento foi partilhado com outros dentistas.",
      },
      {
        label: "Detalhes por Dentista",
        description:
          "Cada dentista pode ser expandido para ver a lista completa dos tratamentos executados, despesas pagas e materiais utilizados que compõem os totais.",
      },
      {
        label: "Dentistas Inativos",
        description:
          "Dentistas que já não estão ativos na clínica continuam a aparecer no relatório se tiveram trabalho no período, identificados com a etiqueta 'Inativo'. O trabalho realizado nunca se perde.",
      },
      {
        label: "Exportar Excel",
        description:
          "Gera um ficheiro com várias folhas: um resumo geral por dentista e folhas separadas com o detalhe dos tratamentos, tratamentos isolados, despesas e materiais utilizados.",
      },
    ],
    icon: <BarChart3 />,
    severity: "info",
  },
  orcamentosAltoRisco: {
    title: "Relatório de Orçamentos de Alto Risco",
    description:
      "Este relatório identifica clientes em situação de alto risco financeiro, onde o valor dos tratamentos executados supera em mais de 50% o valor pago. Essencial para controle de fluxo de caixa e prevenção de perdas financeiras.",
    detailedInfo: [
      {
        label: "Alto Risco",
        description:
          "Cliente deve mais de 50% do que já pagou (diferença negativa > 50% do valor pago)",
        color: "#d32f2f",
      },
      {
        label: "Cálculo de Risco",
        description:
          "Risco = (Valor Executado - Valor Pago). Se negativo e > 50% do pago = ALTO RISCO",
        color: "#f44336",
      },
      {
        label: "Valor Executado",
        description:
          "Soma dos valores de procedimentos marcados como completos no plano (busca valor_cliente no orçamento)",
        color: "#ff5722",
      },
      {
        label: "Valor Pago",
        description:
          "Soma de todas as parcelas pagas (parcelaPaga: true) da conta corrente",
        color: "#ff9800",
      },
      {
        label: "Percentual de Risco",
        description:
          "Badge colorido indicando gravidade: 50-75% (laranja), 75-100% (vermelho), 100-150% (vermelho escuro), >150% (vermelho muito escuro)",
      },
      {
        label: "Detalhes por Orçamento",
        description:
          "Accordion expansível mostrando breakdown de cada orçamento do cliente em risco",
      },
      {
        label: "Link Direto",
        description: "Clique no nome para ir à página de débitos do cliente",
      },
      {
        label: "Exportar Excel",
        description:
          "Gera arquivo com 3 abas: Resumo, Clientes em Alto Risco, e Detalhes por Orçamento",
      },
    ],
    icon: <AlertTriangle />,
    severity: "error",
  },
  sms: {
    title: "Relatório de SMS",
    description:
      "Este relatório apresenta o histórico de mensagens SMS enviadas pela clínica no período selecionado, permitindo monitorizar comunicações com pacientes e acompanhar o consumo de créditos.",
    detailedInfo: [
      {
        label: "Saldo Disponível",
        description:
          "Crédito atual da clínica disponível para envio de SMS, apresentado em ECV.",
        color: "#4caf50",
      },
      {
        label: "Total Gasto",
        description:
          "Valor total já consumido pela clínica em envios de SMS desde o início.",
        color: "#f44336",
      },
      {
        label: "SMS no Período",
        description:
          "Quantidade de mensagens enviadas dentro do intervalo de datas selecionado.",
        color: "#1976d2",
      },
      {
        label: "Status",
        description:
          "Indica o estado do envio: Enviado (sucesso), Pendente (aguarda confirmação) ou Falhou (erro no envio).",
      },
      {
        label: "Tipo de Agendamento",
        description:
          "Origem do SMS: Agenda, AgendaTratamento ou TratamentoIsolado.",
      },
      {
        label: "Tentativas",
        description:
          "Número de tentativas de envio realizadas pelo sistema para aquela mensagem.",
      },
      {
        label: "Link Direto",
        description:
          "Clique no nome do cliente para aceder à sua página de perfil.",
      },
      {
        label: "Exportar Excel",
        description:
          "Gera um ficheiro com o histórico completo de SMS do período, incluindo saldo e total gasto.",
      },
    ],
    icon: <MessageSquare />,
    severity: "info",
  },
  procedimentosExecutados: {
    title: "Procedimentos Executados",
    description:
      "Este relatório apresenta o número de procedimentos executados na clínica no período selecionado, agrupados por categoria de tratamento e detalhados por tipo de tratamento. Distingue ainda os procedimentos realizados em planos de tratamento dos tratamentos isolados.",
    detailedInfo: [
      {
        label: "Total de Procedimentos",
        description:
          "Número total de procedimentos executados no período, somando planos de tratamento e tratamentos isolados.",
        color: "#1976d2",
      },
      {
        label: "Em Plano de Tratamento",
        description:
          "Procedimentos concluídos dentro de planos de tratamento (marcados como completos) no período.",
        color: "#4caf50",
      },
      {
        label: "Tratamentos Isolados",
        description:
          "Procedimentos executados fora de um plano, registados como tratamentos isolados atendidos no período.",
        color: "#9c27b0",
      },
      {
        label: "Por Categoria",
        description:
          "Cada categoria de tratamento é apresentada como subtotal, com a respetiva contagem em plano, isolados e total.",
      },
      {
        label: "Detalhe por Tratamento",
        description:
          "Por baixo de cada categoria, são listados os tratamentos individuais com o número de execuções de cada um.",
      },
      {
        label: "Peso",
        description:
          "Percentagem que cada categoria representa face ao total de procedimentos executados no período.",
      },
      {
        label: "Exportar Excel",
        description:
          "Gera um ficheiro com a contagem por categoria e, indentadas, as contagens de cada tratamento.",
      },
    ],
    icon: <ClipboardCheck />,
    severity: "info",
  },

  email: {
    title: "Relatório de Emails",
    description:
      "Este relatório apresenta o histórico de emails enviados pela clínica no período selecionado, permitindo monitorizar comunicações com pacientes e acompanhar taxas de entrega e falha.",
    detailedInfo: [
      {
        label: "Total de Envios",
        description:
          "Número de emails distintos enviados no período selecionado.",
        color: "#1976d2",
      },
      {
        label: "Total Destinatários",
        description:
          "Soma de todos os destinatários incluídos nos envios do período.",
        color: "#7b1fa2",
      },
      {
        label: "Entregues",
        description:
          "Total de destinatários que receberam o email com sucesso.",
        color: "#4caf50",
      },
      {
        label: "Falhados",
        description: "Total de destinatários para os quais o envio falhou.",
        color: "#f44336",
      },
      {
        label: "Status",
        description:
          "Indica o estado do envio: Completo (todos entregues), Parcial (alguns falharam) ou Falhou (nenhum entregue).",
      },
      {
        label: "Detalhes por Destinatário",
        description:
          "Accordion expansível mostrando nome, email e sucesso de entrega para cada destinatário do envio.",
      },
      {
        label: "Exportar Excel",
        description:
          "Gera ficheiro com 2 abas: Resumo com totais gerais e Histórico com cada envio detalhado.",
      },
    ],
    icon: <Mail />,
    severity: "info",
  },

  dfesPorEmitir: {
    title: "Relatório de Documentos Fiscais Eletrónicos Por Emitir",
    description:
      "Este relatório identifica todas as transações financeiras (parcelas de contas correntes e entradas) que já foram recebidas mas ainda não têm o respetivo documento fiscal eletrónico emitido, permitindo regularizar a situação fiscal da clínica.",
    detailedInfo: [
      {
        label: "Contas Correntes",
        description:
          "Parcelas pagas de orçamentos aprovados que necessitam de FRE (Fatura-Recibo Eletrónica)",
      },
      {
        label: "Entradas Cliente",
        description:
          "Entradas já pagas dos clientes que necessitam de FRE ou TVE",
      },
      {
        label: "Cliente/Financeiro - FTE",
        description:
          "Transações com clientes/financeiros que ainda não têm FTE (Fatura Eletrónica) emitida",
      },
      {
        label: "Cliente/Financeiro - RCE",
        description:
          "Transações já faturadas e recebidas que aguardam emissão de RCE (Recibo Eletrónico)",
      },
      {
        label: "Exportar",
        description:
          "Permite gerar um arquivo Excel separando por tipo de documento a emitir",
      },
      {
        label: "Filtrar por Data",
        description:
          "Os resultados podem ser filtrados por data de pagamento ou data limite de pagamento",
      },
    ],
    icon: <Receipt />, // ou <DocumentScannerIcon /> ou <DescriptionIcon />
    severity: "warning", // 'warning' porque são DFEs pendentes
  },

  // Relatório de Credenciais
  credenciais: {
    title: "Relatório de Credenciais para Instituições",
    description:
      "Este relatório exibe os orçamentos com cobertura institucional (INPS, BCA, etc.) dentro do período selecionado. Permite filtrar, exportar e marcar orçamentos como enviados à instituição, gerando relatórios formatados conforme os padrões exigidos.",

    columns: [
      {
        name: "Status",
        description:
          "Indica o estado atual do orçamento: Enviado (já enviado à instituição), Pronto (todos procedimentos cobertos completos), Parcial (alguns completos), Pendente (nenhum completo), Interrompido (plano de tratamento foi interrompido), Terminado ou Em Andamento (para orçamentos sem cobertura).",
      },
      {
        name: "Nome",
        description:
          "Nome fiscal do beneficiário/cliente. Clique para acessar a ficha completa do cliente.",
      },
      {
        name: "Nr Credencial",
        description:
          "Número da credencial/autorização fornecida pela instituição. Use o ícone ✏️ para editar o número, a data de emissão ou a categoria do beneficiário sem sair desta tela.",
      },
      {
        name: "Dt Emissão",
        description:
          "Data de emissão da credencial pela instituição. Usada para calcular se a credencial está expirada. A coloração é recalculada automaticamente quando editas a data.",
      },
      {
        name: "Dt Início",
        description:
          "Data de início do tratamento. Se não houver data no plano, mostra a data do primeiro procedimento executado.",
      },
      {
        name: "Dt Término",
        description:
          "Data apresentada conforme o status do orçamento: (1) Orçamentos terminados: data de conclusão oficial; (2) Orçamentos em andamento ou interrompidos: data do último procedimento executado; (3) Orçamentos sem execuções: mostra 'Em Andamento'.",
      },
      {
        name: "Total Beneficiário",
        description:
          "Valor total pago pelo beneficiário, incluindo tratamentos E próteses, com a majoração já descontada. Para orçamentos interrompidos, inclui apenas procedimentos efetivamente executados (com majoração proporcional). Atenção: ao exportar, este valor pode ser substituído pelo valor 'só executados' caso a opção esteja ativa nas configurações de exportação.",
      },
      {
        name: "Total Instituição",
        description:
          "Valor total de comparticipação da instituição, incluindo tratamentos E próteses, com a majoração já acrescida. Para orçamentos interrompidos, inclui apenas procedimentos efetivamente executados (com majoração proporcional). Atenção: ao exportar, este valor pode ser substituído pelo valor 'só executados' caso a opção esteja ativa nas configurações de exportação.",
      },
      {
        name: "Desconto",
        description:
          "Desconto aplicado ao orçamento (valor fixo). Não afeta os cálculos de beneficiário/instituição na tabela.",
      },
      {
        name: "Majoração",
        description:
          "Majoração aplicada (15% sobre a parte da instituição). Este valor é SUBTRAÍDO do Total Beneficiário e ADICIONADO ao Total Instituição automaticamente. Para orçamentos interrompidos, é mostrada a majoração proporcional aos procedimentos executados.",
      },
      {
        name: "Data Envio",
        description:
          "Data em que o orçamento foi marcado como enviado à instituição. Aparece apenas no filtro 'Enviados'.",
      },
      {
        name: "Nº Fatura",
        description:
          "Número da fatura associada ao envio para a instituição. Aparece apenas no filtro 'Enviados'.",
      },
    ],

    filters: [
      {
        name: "Período",
        description:
          "Intervalo de datas para filtrar orçamentos. Considera a data de emissão da credencial.",
      },
      {
        name: "Instituição",
        description:
          "Selecione a instituição específica (INPS, BCA, etc.) para filtrar apenas orçamentos com cobertura dessa entidade.",
      },
      {
        name: "Status dos Orçamentos",
        options: [
          {
            value: "Todos",
            description:
              "Exibe todos os orçamentos não enviados, independente do status de execução.",
          },
          {
            value: "Prontos para Enviar",
            description:
              "Orçamentos onde todos os procedimentos cobertos pela instituição foram completados. Prontos para faturação.",
          },
          {
            value: "Em Andamento",
            description:
              "Orçamentos com procedimentos cobertos ainda pendentes de execução.",
          },
          {
            value: "Expirados",
            description:
              "Orçamentos cuja credencial foi emitida há mais de 180 dias (6 meses).",
          },
          {
            value: "Enviados",
            description:
              "Orçamentos já marcados como enviados à instituição. Mostra colunas adicionais: Data de Envio e Nº Fatura. Permite desmarcar como enviado caso necessário.",
          },
        ],
      },
      {
        name: "Busca",
        description:
          "Digite para filtrar em tempo real por nome do beneficiário ou número de credencial. A busca aceita acentuação flexível e palavras parciais (com debounce para melhor desempenho).",
      },
    ],

    features: [
      {
        title: "Exportação Flexível",
        description:
          "Exporte todos os orçamentos ou apenas os selecionados. Configure quantos orçamentos por folha e se deseja organizar por categoria de beneficiário (TCO, TCP, PA, MBCSS).",
      },
      {
        title: "Calcular apenas procedimentos executados",
        description:
          "Opção disponível na exportação. Quando ativa (padrão), orçamentos em andamento parcialmente executados são exportados com o valor proporcional aos procedimentos já realizados (com majoração recalculada). Orçamentos terminados, interrompidos, enviados ou sem nenhum procedimento executado usam sempre o valor original do orçamento.",
      },
      {
        title: "Marcação como Enviado",
        description:
          "Selecione orçamentos, informe o número da fatura (sugerido automaticamente com base no último FTE emitido) e mês de referência, e marque como enviados. O sistema gera automaticamente o relatório Excel formatado.",
      },
      {
        title: "Desmarcar Enviados",
        description:
          "No filtro 'Enviados', selecione orçamentos e use 'Marcar como Não Enviado' para reverter o envio. Útil quando há erros no envio à instituição.",
      },
      {
        title: "Edição de Credencial",
        description:
          "Edita o número da credencial, data de emissão e categoria do beneficiário diretamente na tabela através do ícone ✏️. As alterações refletem imediatamente sem necessidade de recarregar.",
      },
      {
        title: "Comprovativo de Desconto",
        description:
          "Gera PDFs de comprovativos para orçamentos selecionados que tenham desconto aplicado.",
      },
      {
        title: "Carta de Apresentação",
        description:
          "Gera uma carta de apresentação em PDF para a instituição selecionada, com a data escolhida.",
      },
      {
        title: "Seleção Inteligente",
        description:
          "Apenas orçamentos não enviados podem ser selecionados (exceto no filtro 'Enviados', onde a seleção serve para desmarcar). A seleção é preservada mesmo ao filtrar por busca.",
      },
    ],

    tips: [
      "💡 Credenciais a vermelho (≥6 meses) estão expiradas e laranja (≥5 meses) estão próximas de expirar.",
      "💡 Use o filtro 'Prontos para Enviar' para identificar rapidamente orçamentos completados e faturáveis.",
      "💡 Para orçamentos com cobertura parcial (MISTO), o sistema calcula apenas os valores dos procedimentos cobertos pela instituição.",
      "💡 Orçamentos interrompidos mostram o contador de procedimentos cobertos executados/total (ex: 3/5). Apenas os procedimentos marcados como completos no plano de tratamento são incluídos nos totais financeiros - a majoração também é recalculada proporcionalmente.",
      "💡 A exportação organizada por categoria facilita a análise e organização dos relatórios por tipo de beneficiário.",
      "💡 Use a busca para localizar rapidamente um paciente específico ou número de credencial.",
      "💡 Se a data de início do tratamento for ≥30 dias após a emissão da credencial, a data aparece a roxo - revise se está correto.",
      "💡 A opção 'calcular apenas procedimentos executados' (ativa por padrão) só afeta orçamentos em andamento parcialmente executados. Para os outros casos (terminados, interrompidos, sem execução, enviados) o valor exportado é sempre o do orçamento.",
      "💡 Majoração de 15%: quando presente, é automaticamente transferida do total do beneficiário para o total da instituição nos cálculos e relatórios.",
      "💡 Os totais de Beneficiário e Instituição consolidam tratamentos + próteses em uma única coluna para simplificação.",
    ],

    detailedInfo: [
      {
        label: "Vermelho (Datas)",
        description: "Credenciais com mais de 6 meses - EXPIRADAS",
        color: "#f44336",
      },
      {
        label: "Laranja (Datas)",
        description: "Credenciais com mais de 5 meses - Próximas de expirar",
        color: "#ff9800",
      },
      {
        label: "Roxo (Datas)",
        description: "Início 30+ dias após emissão da credencial",
        color: "#9C27B0",
      },
      {
        label: "Verde (Status)",
        description: "Badge 'Pronto' - Todos procedimentos cobertos completos",
        color: "#4caf50",
      },
      {
        label: "Laranja (Status)",
        description:
          "Badge 'Parcial' - Alguns procedimentos cobertos completos",
        color: "#ff9800",
      },
      {
        label: "Cinza (Status)",
        description:
          "Badge 'Pendente' (nenhum procedimento coberto completo) ou 'Enviado' (já enviado à instituição)",
        color: "#9e9e9e",
      },
      {
        label: "Vermelho (Status)",
        description:
          "Badge 'Interrompido' - Orçamento foi interrompido (valores refletem apenas procedimentos executados, com majoração proporcional)",
        color: "#f44336",
      },
      {
        label: "Fundo Cinza",
        description: "Orçamentos já enviados à instituição",
        color: "#eeeeee",
      },
    ],

    icon: <Receipt />,
    severity: "info",
  },
  // Relatório de Aprovações de Orçamentos
  aprovacoes: {
    title: "Relatório de Aprovações de Orçamentos",
    description:
      "Este relatório mostra todos os orçamentos aprovados no período selecionado, com detalhes de data, valor, benefício e médico responsável.",
    detailedInfo: [
      {
        label: "Exportar",
        description:
          "Permite gerar um arquivo Excel com todos os dados de aprovações",
      },
      {
        label: "Filtrar",
        description:
          "Os resultados podem ser filtrados por tipo de benefício e médico",
      },
    ],
    icon: <FileText />,
    severity: "info",
  },

  // Relatório de Aprovações de Orçamentos
  urgencias: {
    title: "Relatório de Urgências",
    description:
      "Quantas pessoas chegaram ao balcão a precisar de ser vistas, por que motivo, quanto tempo esperaram, e quantas ficaram sem resposta.",
    detailedInfo: [
      {
        label: "Sem resposta",
        description:
          "É o número que vale a pena olhar: pessoas que chegaram com dor e a quem ninguém chegou — nem as atendeu, nem as dispensou. O dia acabou e o sistema fechou-as. Uma urgência não fica aberta de um dia para o outro: ficaria a dizer que alguém está à espera quando já não está, e impedia marcar uma urgência nova ao mesmo doente.",
        color: "#b91c1c",
      },
      {
        label: "Dispensada",
        description:
          "Alguém a tirou da fila sem atender — o doente foi embora, ou foi marcada por engano. Fica com o nome de quem o fez.",
        color: "#757575",
      },
      {
        label: "Escalou",
        description:
          "Ninguém a assumiu nos primeiros 15 minutos e o aviso teve de ser repetido e alargado à secretária e ao gerente. Mesmo que tenha sido atendida depois, é sinal de que o primeiro aviso não chegou a quem devia.",
        color: "#f57c00",
      },
      {
        label: "Espera média",
        description:
          "Conta APENAS as urgências atendidas — é o tempo entre o doente chegar e um dentista dizer «vou atender agora». Nas que ficaram sem resposta o momento do fecho é o fim do dia, e isso não é tempo de espera de ninguém: incluí-las fazia a média parecer catastrófica por uma razão que não tem a ver com esperar.",
        color: "#2e7d32",
      },
      {
        label: "«Atendida» quer dizer assumida",
        description:
          "O botão da fila diz «Vou atender agora». A hora registada é a de quem pegou na urgência, não a do fim da consulta — o que foi feito ao doente fica no registo clínico do dia.",
      },
      {
        label: "Exportar",
        description:
          "Gera um Excel com duas folhas: a lista completa e o resumo por motivo, desfecho, unidade, quem atendeu e quem marcou.",
      },
    ],
    icon: <AlertTriangle />,
    severity: "info",
  },

  pesquisaAvaliacoes: {
    title: "Relatório de Pesquisa de Avaliações",
    description:
      "Lista as avaliações ativas de um período e diz, de cada uma, o que veio depois: quantos procedimentos foram levantados, quantos chegaram a orçamento e por quanto. Responde à pergunta que a clínica faz ao fim do mês — avaliou-se muito, deu em quê?",
    detailedInfo: [
      {
        label: "Apenas dados ativos",
        description:
          "Avaliações desativadas não entram, nem na lista nem nos totais. Uma avaliação desativada foi apagada do ponto de vista da clínica.",
        color: "#1976d2",
      },
      {
        label: "Criado por / Médico responsável",
        description:
          "Escolha múltipla. As listas são tiradas dos dados do período, por isso incluem quem já saiu da clínica — o trabalho antigo continua lá e continua a poder ser isolado. Ao lado de cada nome está quantos registos tem.",
        color: "#2e7d32",
      },
      {
        label: "Com orçamento",
        description:
          "Não é o mesmo que «Transformado». Transformado é uma marca que fica para sempre; «com orçamento» olha para os orçamentos que estão ATIVOS agora. Uma avaliação transformada cujo orçamento foi depois desativado aparece como «sem orçamento».",
        color: "#f57c00",
      },
      {
        label: "Taxa de conversão",
        description:
          "Percentagem de avaliações do período que chegaram a orçamento ativo. É o número que diz se o problema está em avaliar ou em fechar negócio.",
        color: "#7b1fa2",
      },
      {
        label: "Valor estimado",
        description:
          "A soma dos preços particulares dos tratamentos levantados na avaliação. Não é dívida nem receita — é o que estava em cima da mesa antes de se orçamentar.",
      },
      {
        label: "Clique no nome do cliente",
        description:
          "Abre a ficha do cliente já na aba onde está a avaliação, sem passar pela informação geral.",
      },
      {
        label: "Exportar",
        description:
          "Gera um Excel com duas folhas: a lista completa e o resumo com os agrupamentos por médico, criador, unidade e status. Exporta o que está no ecrã, incluindo a procura escrita na caixa.",
      },
    ],
    icon: <FileText />,
    severity: "info",
  },

  pesquisaOrcamentos: {
    title: "Relatório de Pesquisa de Orçamentos",
    description:
      "Lista os orçamentos ativos de um período com o valor orçamentado, o que já foi recebido e o que falta receber. Agrupa por cobertura, instituição, médico, criador, unidade e mês.",
    detailedInfo: [
      {
        label: "«Por receber» não é «total menos recebido»",
        description:
          "Só conta os orçamentos com conta aberta. Um orçamento que ninguém aprovou não é uma dívida — talvez nunca seja feito. Esse valor aparece à parte, no aviso do cartão «Por receber», e o estado de pagamento dele é «Sem conta», não «Pendente».",
        color: "#d32f2f",
      },
      {
        label: "Apenas dados ativos",
        description:
          "Orçamentos desativados não entram, nem na lista nem nos totais.",
        color: "#1976d2",
      },
      {
        label: "Recebido",
        description:
          "Soma das parcelas efetivamente pagas, incluindo as marcadas como ocultas: uma parcela oculta foi paga como qualquer outra — a marca só silencia o aviso de documento em falta.",
        color: "#2e7d32",
      },
      {
        label: "Cobertura",
        description:
          "Particular, Segurado ou Misto, com a instituição ao lado. Os valores são os que ficaram gravados no orçamento, não os preços de hoje no catálogo — é isso que faz o resumo bater com a conta do cliente.",
        color: "#7b1fa2",
      },
      {
        label: "Cobertura da instituição",
        description:
          "O que a instituição paga. No regime de reembolso o cliente paga tudo à clínica e vai buscar esse valor depois, por isso não se abate ao total — mostra-se ao lado.",
        color: "#f57c00",
      },
      {
        label: "Taxa de aprovação e taxa de cobrança",
        description:
          "A primeira diz quantos orçamentos foram aprovados. A segunda, quanto do que foi aprovado já entrou. Juntas separam um problema de vendas de um problema de cobrança.",
      },
      {
        label: "Clique no nome do cliente",
        description:
          "Abre a ficha do cliente já na aba onde está o orçamento.",
      },
      {
        label: "Exportar",
        description:
          "Gera um Excel com duas folhas: a lista completa, com 29 colunas, e o resumo com todos os agrupamentos. Exporta o que está no ecrã, incluindo a procura escrita na caixa.",
      },
    ],
    icon: <Receipt />,
    severity: "info",
  },

  //Relatorio de procedimentos executados por medico
  "procedimentos-medico": {
    title: "Relatório de Procedimentos Executados por Médico",
    description:
      "Este relatório apresenta todos os procedimentos executados pelos médicos no período selecionado, agregando informações de sessões de planos de tratamento, tratamentos isolados e evoluções de agendamentos em transição.",
    detailedInfo: [
      {
        label: "Sessões de Plano",
        description:
          "Sessões individuais executadas dentro de planos de tratamento",
        color: "#1976d2",
      },
      {
        label: "Tratamentos Isolados",
        description: "Tratamentos executados fora de um plano de tratamento",
        color: "#7b1fa2",
      },
      {
        label: "Evoluções",
        description:
          "Procedimentos registrados como evoluções (agendamentos em transição)",
        color: "#f57c00",
      },
      {
        label: "Filtros Disponíveis",
        description: "Filtrar por médicos específicos e tipos de execução",
      },
      {
        label: "Exportar",
        description:
          "Gerar arquivo Excel com todos os procedimentos e resumo por médico",
      },
    ],
    icon: <Stethoscope />,
    severity: "info",
  },

  inatividade: {
    title: "Relatório de Clientes Inativos",
    description:
      "Este relatório identifica clientes que não registam atividade clínica há mais do período definido, classificando-os por categoria de inatividade. Permite priorizar ações de reativação e envio de comunicações direcionadas.",
    detailedInfo: [
      {
        label: "Fiéis Inativos",
        description:
          "Clientes que completaram pelo menos um plano de tratamento mas não voltaram à clínica. Não possuem nenhum plano ativo em curso. São os melhores candidatos a reativação pois já concluíram tratamentos com sucesso.",
        color: "#2e7d32",
      },
      {
        label: "Tratamentos Abandonados",
        description:
          "Clientes com planos de tratamento incompletos e sem atividade recente. Inclui planos marcados como interrompidos apenas quando não existe continuidade posterior (novo plano, orçamento ou tratamento isolado executado após a interrupção).",
        color: "#c62828",
      },
      {
        label: "Esporádicos",
        description:
          "Clientes que realizaram tratamentos isolados ou consultas avulsas mas nunca aderiram a um plano de tratamento. Excluem-se agendamentos de avaliação e agendamentos provisórios associados a orçamentos por aprovar.",
        color: "#0277bd",
      },
      {
        label: "Período de Inatividade",
        description:
          "Filtro configurável entre 30 dias e 1 ano. Define o tempo mínimo sem atividade para que um cliente seja incluído no relatório. O padrão é 90 dias.",
      },
      {
        label: "Inativo há",
        description:
          "Badge colorido indicando a gravidade da inatividade: informativo (menos de 30 dias), aviso (1 a 6 meses), erro (mais de 6 meses ou mais de 1 ano).",
      },
      {
        label: "Planos Interrompidos",
        description:
          "Planos marcados como INTERROMPIDO por razões administrativas (ex: cobertura negada pelo INPS) são excluídos do abandono quando existe evidência de continuidade de tratamento posterior, seja por novo plano, orçamento ou tratamento isolado executado.",
      },
      {
        label: "Seleção e Envio de Email",
        description:
          "Permite selecionar múltiplos clientes na tabela e enviar comunicações em massa. O botão indica quantos clientes selecionados possuem email registado.",
      },
      {
        label: "Incluir Tratamentos Isolados",
        description:
          "Opção para incluir ou excluir a categoria Esporádicos do relatório. Útil quando se pretende focar apenas em clientes com histórico de planos de tratamento.",
      },
    ],
    icon: <UserX />, // ou o ícone que usares para inatividade
    severity: "warning",
  },

  inadimplencia: {
    title: "Relatório de Inadimplência",
    description:
      "Este relatório apresenta todos os clientes que já terminaram os seus tratamentos mas ainda possuem valores pendentes de pagamento. Permite identificar rapidamente casos que necessitam de cobrança e oferece métricas de comportamento de pagamento.",
    detailedInfo: [
      {
        label: "Score de Pagamento",
        description:
          "O score de pagamento é uma métrica de 0 a 100 que reflete o comportamento histórico de pagamento de um cliente. A base do score parte da percentagem de parcelas pagas dentro do prazo. Clientes que pagaram 80% ou mais das parcelas a tempo começam com um score alto (entre 85 e 100). Quem pagou entre 50% e 79% a tempo fica numa faixa média (entre 50 e 71). Abaixo disso, ou sem histórico de pagamentos, o score é baixo. A esse valor base são aplicadas penalizações conforme a antiguidade do atraso mais antigo em aberto: 10 pontos para atrasos superiores a 30 dias, 15 para mais de 60 dias, e 20 pontos para atrasos superiores a 90 dias. O score final indica, de forma rápida, se o cliente tem um perfil pontual (score alto), regular (score médio) ou irregular (score baixo).",
        color: "#1976d2",
      },
      {
        label: "Alertas de Cobrança",
        description:
          "Identifica automaticamente casos urgentes (atrasos > 30 dias)",
        color: "#f44336",
      },
      {
        label: "Contas Interrompidas",
        description: "Podem ser incluídas ou filtradas conforme necessidade",
        color: "#ff9800",
      },
      {
        label: "Análise Comportamental",
        description:
          "Classifica clientes como pontuais, regulares ou irregulares",
      },
      {
        label: "Exportar Excel",
        description:
          "Gera relatório completo com todas as inadimplências e resumo geral",
      },
    ],
    icon: <AlertTriangle />,
    severity: "warning",
  },

  "relatorio-diario": {
    title: "Relatório Diário da Clínica",
    description:
      "Este relatório apresenta um resumo completo de todas as atividades da clínica em um dia específico, incluindo atendimentos realizados, procedimentos executados, medicamentos prescritos e pagamentos recebidos.",
    detailedInfo: [
      {
        label: "Atendimentos",
        description:
          "Lista todos os clientes atendidos no dia, independente do tipo de agendamento",
        color: "#1976d2",
      },
      {
        label: "Procedimentos",
        description:
          "Procedimentos executados de planos de tratamento e tratamentos isolados",
        color: "#2e7d32",
      },
      {
        label: "Evoluções",
        description: "Evoluções registradas nos agendamentos de transição",
        color: "#f57c00",
      },
      {
        label: "Prescrições",
        description: "Medicamentos prescritos no dia com detalhes de posologia",
        color: "#7b1fa2",
      },
      {
        label: "Pagamentos",
        description:
          "Todos os pagamentos recebidos (conta corrente e entradas)",
        color: "#d32f2f",
      },
      {
        label: "Exportar Excel",
        description:
          "Gera arquivo com 6 abas: resumo, clientes, atendimentos, procedimentos, medicamentos e pagamentos",
      },
    ],
    icon: <FileText />,
    severity: "info",
  },
  // Adicione mais tipos de relatórios conforme necessário
};

const InfoRelatorio = ({ tipoRelatorio = "" }) => {
  const infoRelatório =
    informacoesRelatorioPorTipo[tipoRelatorio] ||
    informacoesRelatorioPorTipo.default;

  return (
    <Box sx={{ mb: 3, mt: 2 }}>
      {/* Alert Principal com Título e Descrição */}
      <Alert
        severity={infoRelatório.severity}
        variant="outlined"
        icon={infoRelatório.icon}
        sx={{ mb: 2 }}
      >
        <AlertTitle sx={{ fontWeight: "bold" }}>
          {infoRelatório.title}
        </AlertTitle>
        <Typography variant="body2">{infoRelatório.description}</Typography>
      </Alert>

      {/* Accordions para informações detalhadas */}
      <Box>
        {/* Accordion: Colunas */}
        {infoRelatório.columns && (
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                📊 Colunas do Relatório
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {infoRelatório.columns.map((col, index) => (
                  <Box key={index}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "primary.main" }}
                    >
                      {col.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {col.description}
                    </Typography>
                    {index < infoRelatório.columns.length - 1 && (
                      <Divider sx={{ mt: 1.5 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Accordion: Filtros */}
        {infoRelatório.filters && (
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                🔍 Filtros Disponíveis
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {infoRelatório.filters.map((filter, index) => (
                  <Box key={index}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "primary.main",
                        mb: 0.5,
                      }}
                    >
                      {filter.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {filter.description}
                    </Typography>

                    {/* Opções do filtro */}
                    {filter.options && (
                      <Box
                        sx={{
                          ml: 2,
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                          mt: 1,
                        }}
                      >
                        {filter.options.map((opt, i) => (
                          <Box
                            key={i}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            <Chip
                              label={opt.value}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ minWidth: "fit-content" }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ flex: 1 }}
                            >
                              {opt.description}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {index < infoRelatório.filters.length - 1 && (
                      <Divider sx={{ mt: 1.5 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Accordion: Funcionalidades */}
        {infoRelatório.features && (
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                ⚡ Funcionalidades
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {infoRelatório.features.map((feature, index) => (
                  <Box key={index}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", color: "primary.main" }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.5 }}
                    >
                      {feature.description}
                    </Typography>
                    {index < infoRelatório.features.length - 1 && (
                      <Divider sx={{ mt: 1.5 }} />
                    )}
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Accordion: Dicas */}
        {infoRelatório.tips && (
          <Accordion>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                💡 Dicas e Boas Práticas
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {infoRelatório.tips.map((tip, index) => (
                  <Typography
                    key={index}
                    variant="body2"
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      color: "text.secondary",
                    }}
                  >
                    {tip}
                  </Typography>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Accordion: Legenda de Cores (detailedInfo) */}
        {infoRelatório.detailedInfo && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ChevronDown />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                🎨 Legenda e Informações Adicionais
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {infoRelatório.detailedInfo.map((info, index) => (
                  <Box
                    key={index}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                  >
                    {info.color && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          backgroundColor: info.color,
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography variant="body2">
                      <strong>{info.label}:</strong> {info.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Box>
    </Box>
  );
};

export default InfoRelatorio;
